import { z } from 'zod';

/**
 * Профиль маппинга generic-коннектора (§10.1): как читать файл поставщика и какие
 * его поля соответствуют каноническим полям предложения. Настраивается в админке без кода.
 */

export const IMPORT_FORMATS = ['csv', 'xml', 'json', 'jsonl', 'excel'] as const;
export const TEXT_ENCODINGS = [
  'utf-8',
  'windows-1257',
  'windows-1251',
  'iso-8859-13',
  'iso-8859-1',
] as const;

/** Шаги преобразования значения поля — применяются по порядку. */
export const transformSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('trim') }),
  z.object({ op: z.literal('lowercase') }),
  z.object({ op: z.literal('uppercase') }),
  /** Число из строки: «1 234,56», «1,234.56», «12.5». */
  z.object({ op: z.literal('number') }),
  z.object({ op: z.literal('multiply'), value: z.number() }),
  z.object({ op: z.literal('digits') }),
  z.object({ op: z.literal('split'), separator: z.string().min(1).max(5) }),
  z.object({ op: z.literal('map'), values: z.record(z.string(), z.string()) }),
  z.object({ op: z.literal('default'), value: z.union([z.string(), z.number()]) }),
  z.object({ op: z.literal('prefix'), value: z.string().max(200) }),
  /** Последняя часть пути ссылки: «https://x.lv/lv/a/lampa-bangkok?y=1» → «lampa-bangkok». */
  z.object({ op: z.literal('lastSegment') }),
  /** Часть строки по регулярному выражению — первая группа: EAN в конце ссылки `-(\d{13})\.html`. */
  z.object({ op: z.literal('pattern'), value: z.string().min(1).max(200) }),
]);
export type Transform = z.infer<typeof transformSchema>;

export const fieldMapSchema = z.object({
  /** CSV/Excel — заголовок колонки; XML — путь от записи (`price`, `stock/qty`, `@id`); JSON — путь через точку. */
  source: z.string().min(1).max(200),
  /** Ещё пути, значения которых добавляются к списку (фото: `image` + `gallery/image`). */
  alsoFrom: z.array(z.string().min(1).max(200)).max(5).optional(),
  transforms: z.array(transformSchema).max(10).default([]),
});
export type FieldMap = z.infer<typeof fieldMapSchema>;

export const CANONICAL_FIELDS = [
  'supplierSku',
  'gtin',
  'mpn',
  'brand',
  'title',
  'description',
  'cost',
  'currency',
  'stockQty',
  'leadTimeDays',
  'moq',
  'packSize',
  'imageUrls',
  'category',
  'weightG',
  'lengthMm',
  'widthMm',
  'heightMm',
  'pageUrl',
] as const;
export type CanonicalField = (typeof CANONICAL_FIELDS)[number];

export const mappingProfileSchema = z
  .object({
    format: z.enum(IMPORT_FORMATS),
    encoding: z.enum(TEXT_ENCODINGS).default('utf-8'),
    csv: z
      .object({
        delimiter: z.string().min(1).max(2).default(','),
        quote: z.string().length(1).default('"'),
        /** Сколько строк пропустить до заголовка. */
        skipLines: z.number().int().min(0).max(50).default(0),
      })
      .optional(),
    /** XML: путь элементов до записи, например `catalog/offers/offer`. */
    xml: z.object({ recordPath: z.string().min(1).max(200) }).optional(),
    /** JSON: путь до массива записей (`data.items`); пусто — корень является массивом. */
    json: z.object({ recordPath: z.string().max(200).default('') }).optional(),
    excel: z
      .object({ sheet: z.union([z.number().int().min(1), z.string()]).default(1) })
      .optional(),
    fields: z.object({
      supplierSku: fieldMapSchema,
      cost: fieldMapSchema,
      stockQty: fieldMapSchema,
      gtin: fieldMapSchema.optional(),
      mpn: fieldMapSchema.optional(),
      brand: fieldMapSchema.optional(),
      title: fieldMapSchema.optional(),
      description: fieldMapSchema.optional(),
      currency: fieldMapSchema.optional(),
      leadTimeDays: fieldMapSchema.optional(),
      moq: fieldMapSchema.optional(),
      packSize: fieldMapSchema.optional(),
      imageUrls: fieldMapSchema.optional(),
      category: fieldMapSchema.optional(),
      /** Упаковка: вес, г; коробка, мм. Другие единицы — через transform multiply (кг → г: 1000). */
      weightG: fieldMapSchema.optional(),
      lengthMm: fieldMapSchema.optional(),
      widthMm: fieldMapSchema.optional(),
      heightMm: fieldMapSchema.optional(),
      /** Страница товара у поставщика: вес и коробка берутся из её характеристик, если их нет в файле. */
      pageUrl: fieldMapSchema.optional(),
    }),
    /**
     * Страница товара (поле pageUrl): розничная цена поставщика — первая группа регулярного
     * выражения по HTML; vatPct — НДС, который в неё включён (0 — цена без НДС).
     */
    page: z
      .object({
        retailPricePattern: z.string().min(1).max(300),
        retailVatPct: z.number().min(0).max(50).default(0),
      })
      .optional(),
    defaults: z
      .object({
        currency: z.string().length(3).default('EUR'),
        leadTimeDays: z.number().int().min(0).max(365).optional(),
      })
      .default({ currency: 'EUR' }),
  })
  .refine((p) => p.format !== 'xml' || !!p.xml, {
    message: 'xml.recordPath is required for XML',
    path: ['xml'],
  });
export type MappingProfile = z.infer<typeof mappingProfileSchema>;

/** Настройки источника (интеграции поставщика). */
export const importSourceSchema = z.object({
  name: z.string().trim().min(1).max(200),
  /** full — файл содержит весь ассортимент (отсутствующие позиции обнуляются); delta — только изменения. */
  feedMode: z.enum(['full', 'delta']).default('full'),
  /** Язык названий/описаний в файле поставщика — для черновиков новых товаров. */
  contentLocale: z.enum(['lv', 'lt', 'et', 'ru', 'en']).default('en'),
  transport: z.discriminatedUnion('type', [
    z.object({ type: z.literal('upload') }),
    z.object({
      type: z.literal('url'),
      url: z.url({ protocol: /^https?$/ }),
      /** Как часто забирать файл, минуты. */
      intervalMinutes: z
        .number()
        .int()
        .min(5)
        .max(7 * 24 * 60)
        .default(60),
    }),
  ]),
  mapping: mappingProfileSchema,
  /** Защита от «плохого файла» (§10.2), в процентах от активных предложений. */
  guards: z
    .object({
      maxZeroedStockPct: z.number().min(0).max(100).default(30),
      maxPriceChangedPct: z.number().min(0).max(100).default(30),
      /** Изменение цены позиции больше этого порога считается «сильным». */
      priceChangeThresholdPct: z.number().min(0).max(1000).default(20),
    })
    .default({ maxZeroedStockPct: 30, maxPriceChangedPct: 30, priceChangeThresholdPct: 20 }),
});
export type ImportSourceRequest = z.infer<typeof importSourceSchema>;

export const supplierSchema = z.object({
  code: z.string().regex(/^[a-z0-9][a-z0-9-]{1,63}$/),
  name: z.string().trim().min(1).max(200),
  legalName: z.string().trim().max(200).optional(),
  countryCode: z.string().length(2),
  currency: z.string().length(3).default('EUR'),
  vatId: z.string().max(32).optional(),
  priority: z.number().int().min(0).max(1000).default(0),
  defaultLeadTimeDays: z.number().int().min(0).max(365).default(3),
  staleAfterMinutes: z
    .number()
    .int()
    .min(10)
    .max(7 * 24 * 60)
    .default(1440),
  safetyBuffer: z.number().int().min(0).max(10_000).default(0),
  dropship: z.boolean().default(false),
  dropshipLabelBy: z.enum(['supplier', 'shop']).optional(),
  trackingSource: z.enum(['api', 'file', 'email', 'manual']).optional(),
  neutralPackaging: z.boolean().default(false),
  /** Заказы поставщику передаются вручную: без e-mail и API, сотрудник отмечает «передан». */
  manualOrders: z.boolean().optional(),
});
export type SupplierRequest = z.infer<typeof supplierSchema>;

export const resolveMatchSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('link'), variantId: z.uuid() }),
  z.object({ action: z.literal('create_product'), categoryId: z.uuid().optional() }),
  z.object({ action: z.literal('reject') }),
]);
export type ResolveMatchRequest = z.infer<typeof resolveMatchSchema>;

/** Массовое создание товаров из предложений поставщика: его артикулы и общая категория. */
export const createProductsFromOffersSchema = z.object({
  supplierSkus: z.array(z.string().trim().min(1).max(128)).min(1).max(1000),
  categoryId: z.uuid().optional(),
});
export type CreateProductsFromOffersRequest = z.infer<typeof createProductsFromOffersSchema>;
